import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { updateApiStatus, deleteApiRecord } from 'services/ClientCompanyService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { status, Title } from 'components/helper'
injectReducer('clientCompany', reducer)

export default function ClientCompany() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.clientCompany.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleStatus = (id, data) => {
        updateApiStatus(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Parent Company',
                className: 'min-w-[150px]',
                renderCell: (row) => (
                    <Title
                        title={row?.company?.name}
                        image={row?.company?.company_logo}
                    />
                ),
            },
            {
                field: 'client',
                header: 'Client Company',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.client ? (
                        <Title
                            title={row?.client?.name}
                            image={row?.client?.company_logo}
                        />
                    ) : null,
            },
            {
                field: 'email',
                header: 'Email',
                className: 'min-w-[150px]',
                renderCell: (row) => (row?.client ? row?.client?.email : null),
            },
            {
                field: 'is_active',
                header: 'Status',
                renderCell: (row) => {
                    return checkPermission('company-admin', 'edit') ? (
                        row?.client ? (
                            <ConfirmationModal
                                onConfirm={() => {
                                    let data = { ...row?.client }
                                    data.is_active = !row?.client?.is_active
                                    delete data.company_logo
                                    handleStatus(row.id, {
                                        client: data,
                                        handles_own_transport:
                                            row?.handles_own_transport,
                                    })
                                }}
                            >
                                {status(row?.client?.is_active)}
                            </ConfirmationModal>
                        ) : null
                    ) : row?.client ? (
                        status(row?.client?.is_active)
                    ) : null
                },
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('client-company', 'edit') ? (
                                <Tooltip title={'Edit'}>
                                    <Link
                                        to={`/company-management/client-company/${row?.id}/edit`}
                                        className="hover:text-primary"
                                    >
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('client-company', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row?.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Client Company
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('client-company', 'create')
                        ? '/company-management/client-company/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
